import type { SupabaseClient } from "@supabase/supabase-js";

import {
  CLIENT_ARTIFACT_INTEGRITY, clientArtifactRefusalCopy, isClientArtifactPreparedBy,
  isClientArtifactPrintableLabel,
} from "./client-artifact-contract";
import type {
  CheckpointExceptionCode, ServiceWeekCheckpointRow, ServiceWeekKey, ServiceWeekReceipt, ServiceWeekSummary,
} from "./service-week-approval";
import type { ProofOfServiceHeader } from "./proof-of-service";
import { addServiceWeeks, isServiceInstant, isServiceTimeZone, isServiceWeekStart } from "../lib/service-week";

/** Only the client variant has a confirmed contract. Lock O-01 (Internal) is unanswered
 *  and the host refuses a non-client request, so this union has exactly one member. */
export type DutyActivityVariant = "client";
export type DutyActivityLinkage = "assignment_linked" | "unlinked_by_occurred_at";
export type DutyActivityMediaKind = "photo" | "video" | "audio" | "document" | "signature";
export type DutyActivityMediaCounts = Record<DutyActivityMediaKind, number>;

export interface DutyActivityKey extends ServiceWeekKey {
  variant: DutyActivityVariant;
  includeMediaCounts: boolean;
}
export interface DutyActivityIncident {
  rowOrdinal: number;
  occurredAt: string;
  postLabel: string | null;
  category: "safety" | "security" | "maintenance" | "client_note" | "other";
  severity: "info" | "low" | "medium" | "high" | "critical";
  reviewState: "submitted" | "under_review" | "locked";
  linkage: DutyActivityLinkage;
  /** Null exactly when the request excluded media counts. Never a stand-in for unknown. */
  mediaCounts: DutyActivityMediaCounts | null;
}
export interface DutyActivityReport {
  header: ProofOfServiceHeader;
  variant: DutyActivityVariant;
  approval: { state: "approved"; receipt: ServiceWeekReceipt };
  coverageSummary: ServiceWeekSummary;
  checkpointExceptions: ServiceWeekCheckpointRow[];
  incidents: DutyActivityIncident[];
  mediaTotals: DutyActivityMediaCounts | null;
  completeness: { coverageSummary: true; checkpointExceptions: true; incidentSummary: true; incidentCapture: true };
}
export type DutyActivityUnavailableReason = "target_unavailable" | "source_incomplete" | "too_large" | "unavailable";
type DutyActivityRefusal = { status: "refused"; reason: "week_unapproved" | "no_scheduled_service" };
type DutyActivityUnavailable = { status: "unavailable"; reason: DutyActivityUnavailableReason };
export type DutyActivityReadResult =
  | { status: "ok"; revision: string; report: DutyActivityReport }
  | DutyActivityRefusal
  | DutyActivityUnavailable;
export type DutyActivityRevalidationResult =
  | { status: "current" }
  | { status: "changed" }
  | DutyActivityRefusal
  | DutyActivityUnavailable;

const ARTIFACT = "duty activity report";
export const dutyActivityReportCopy = {
  title: "Duty activity report",
  open: "Duty activity report",
  unavailable: clientArtifactRefusalCopy["R-06"]("Duty activity report"),
  // D-03: the unapproved refusal is the shipped proof-of-service sentence verbatim,
  // with nothing appended that could read as a workaround.
  week_unapproved: clientArtifactRefusalCopy["R-01"]("proof of service"),
  no_scheduled_service: clientArtifactRefusalCopy["R-02"](ARTIFACT),
  source_incomplete: clientArtifactRefusalCopy["R-09"](ARTIFACT),
  noTargets: clientArtifactRefusalCopy["R-04"](ARTIFACT),
  targets_too_large: clientArtifactRefusalCopy["R-05"],
  too_large: clientArtifactRefusalCopy["R-03"],
  changed: "Duty activity facts changed. Preview the week again.",
  printHint: "The PDF downloads directly with the same pages shown in Preview.",
  laterRounds: "Later · patrol round records are not available yet.",
  coverageElsewhere: "Shift-by-shift coverage is on the proof of service for this week.",
  recordsNote: "Full incident records and any separate timestamped SOS logs are available on request.",
  // Evidence counts are frozen at approval and never re-derived from live files,
  // so the page dates them rather than implying they are current.
  evidenceAsOf: "Evidence counts are the files recorded when this week was approved.",
  integrity: CLIENT_ARTIFACT_INTEGRITY,
  approveInTimesheets: "Approve in Timesheets",
  openInTimesheets: "Open in Timesheets",
} as const;

const MEDIA_KINDS = ["photo", "video", "audio", "document", "signature"] as const;
const SUMMARY_KEYS = ["coverageRowCount", "publishedSeatCount", "coveredSeatCount", "uncoveredSeatCount", "missingBoundaryCount", "exceptionCount", "pendingUnscheduledCount", "includedUnscheduledCount", "rejectedUnscheduledCount", "workSegmentCount", "expectedCheckInCount", "verifiedCheckInCount", "missedCheckInCount", "checkpointFactCount", "checkpointExceptionCount", "incidentCount"] as const;
const HEADER_KEYS = ["providerLabel", "clientLabel", "siteLabel", "timeZone", "weekStart", "weekEndExclusive", "preparedByLabel", "preparedByRole", "generatedAt", "dataThroughAt"] as const;
const REPORT_KEYS = ["header", "variant", "approval", "coverageSummary", "checkpointExceptions", "incidents", "mediaTotals", "completeness"] as const;
const INCIDENT_KEYS = ["rowOrdinal", "occurredAt", "postLabel", "category", "severity", "reviewState", "linkage", "mediaCounts"] as const;
const CHECKPOINT_KEYS = ["rowOrdinal", "coverageRowOrdinal", "checkpointOrdinal", "routeLabel", "checkpointLabel", "kind", "validationStatus", "recordedAt"] as const;
const CHECKPOINT_CODES = ["duplicate", "out_of_window", "review_required", "offline_recorded", "manual_recorded", "fallback_used", "rejected", "out_of_plan"] as const;
const CATEGORIES = ["safety", "security", "maintenance", "client_note", "other"] as const;
const SEVERITIES = ["info", "low", "medium", "high", "critical"] as const;
const REVIEW_STATES = ["submitted", "under_review", "locked"] as const;

const unavailable = () => ({ status: "unavailable", reason: "unavailable" } as const);
const record = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);
const exact = (v: Record<string, unknown>, keys: readonly string[]) => Object.keys(v).length === keys.length && keys.every((key) => Object.hasOwn(v, key));
const uuid = (v: unknown): v is string => typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const revision = (v: unknown): v is string => typeof v === "string" && /^[0-9a-f]{64}$/.test(v);
const count = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
const ordinal = (v: unknown): v is number => count(v) && v > 0;
const oneOf = <T extends string>(v: unknown, values: readonly T[]): v is T => typeof v === "string" && (values as readonly string[]).includes(v);

export function isDutyActivityKey(key: DutyActivityKey): boolean {
  return uuid(key.accountId) && uuid(key.clientId) && uuid(key.siteId) && isServiceWeekStart(key.weekStart)
    && key.variant === "client" && typeof key.includeMediaCounts === "boolean";
}

function isMediaCounts(value: unknown): value is DutyActivityMediaCounts {
  return record(value) && exact(value, MEDIA_KINDS) && MEDIA_KINDS.every((kind) => count(value[kind]));
}
function mediaTotal(counts: DutyActivityMediaCounts): number {
  return MEDIA_KINDS.reduce((total, kind) => total + counts[kind], 0);
}

function isSummary(value: unknown): value is ServiceWeekSummary {
  if (!record(value) || !exact(value, SUMMARY_KEYS) || !SUMMARY_KEYS.every((key) => count(value[key]))) return false;
  const read = (key: typeof SUMMARY_KEYS[number]): number => typeof value[key] === "number" ? value[key] : -1;
  return read("coverageRowCount") > 0 && read("coverageRowCount") <= 500 && read("workSegmentCount") <= 5000
    && read("checkpointFactCount") <= 10000 && read("incidentCount") <= 200
    && read("pendingUnscheduledCount") === 0 && read("rejectedUnscheduledCount") === 0
    && read("publishedSeatCount") === read("coveredSeatCount") + read("uncoveredSeatCount")
    && read("coverageRowCount") === read("publishedSeatCount") + read("includedUnscheduledCount")
    && read("expectedCheckInCount") === read("verifiedCheckInCount") + read("missedCheckInCount")
    && read("checkpointFactCount") === read("expectedCheckInCount") + read("checkpointExceptionCount");
}

/** Exactly the exception subset: a required checkpoint that is missing, or a recorded
 *  exception. A device-verified row in this array means the host built the wrong set. */
function isCheckpointException(value: unknown): value is ServiceWeekCheckpointRow {
  if (!record(value) || !ordinal(value.rowOrdinal) || !ordinal(value.coverageRowOrdinal)
    || !isClientArtifactPrintableLabel(value.routeLabel) || !isClientArtifactPrintableLabel(value.checkpointLabel)) return false;
  if (value.kind === "required") {
    return exact(value, CHECKPOINT_KEYS) && ordinal(value.checkpointOrdinal)
      && value.validationStatus === "missing" && value.recordedAt === null;
  }
  return exact(value, [...CHECKPOINT_KEYS, "exceptionCode"]) && value.kind === "recorded_exception"
    && value.validationStatus === "recorded_exception" && isServiceInstant(value.recordedAt)
    && oneOf<CheckpointExceptionCode>(value.exceptionCode, CHECKPOINT_CODES)
    && (value.exceptionCode === "out_of_plan" ? value.checkpointOrdinal === null : ordinal(value.checkpointOrdinal));
}

// D-02/D-11: the host owns week attribution. These are only the outer bounds an honest
// frozen occurrence can sit at, because a site-local Monday boundary is at most 14 hours
// before, and a site-local week end at most 12 hours after, UTC midnight on that date.
const MAX_ZONE_AHEAD_MS = 14 * 3_600_000;
const MAX_ZONE_BEHIND_MS = 12 * 3_600_000;

function isIncident(value: unknown, includeMediaCounts: boolean, weekStart: string, weekEndExclusive: string): value is DutyActivityIncident {
  if (!record(value) || !exact(value, INCIDENT_KEYS) || !ordinal(value.rowOrdinal) || !isServiceInstant(value.occurredAt)
    || !(value.postLabel === null || isClientArtifactPrintableLabel(value.postLabel))
    || !oneOf(value.category, CATEGORIES) || !oneOf(value.severity, SEVERITIES) || !oneOf(value.reviewState, REVIEW_STATES)
    || !oneOf<DutyActivityLinkage>(value.linkage, ["assignment_linked", "unlinked_by_occurred_at"])) return false;
  const occurred = Date.parse(value.occurredAt);
  if (occurred < Date.parse(`${weekStart}T00:00:00Z`) - MAX_ZONE_AHEAD_MS
    || occurred >= Date.parse(`${weekEndExclusive}T00:00:00Z`) + MAX_ZONE_BEHIND_MS) return false;
  return includeMediaCounts ? isMediaCounts(value.mediaCounts) : value.mediaCounts === null;
}

function isReport(value: unknown, key: DutyActivityKey): value is DutyActivityReport {
  if (!record(value) || !exact(value, REPORT_KEYS) || value.variant !== "client"
    || !record(value.header) || !exact(value.header, HEADER_KEYS)
    || !record(value.approval) || !exact(value.approval, ["state", "receipt"]) || value.approval.state !== "approved"
    || !record(value.approval.receipt) || !exact(value.approval.receipt, ["approvalState", "approvedAt", "summary"])
    || !record(value.completeness) || !exact(value.completeness, ["coverageSummary", "checkpointExceptions", "incidentSummary", "incidentCapture"])
    || !Object.values(value.completeness).every((flag) => flag === true)) return false;
  const header = value.header;
  const weekEndExclusive = addServiceWeeks(key.weekStart, 1);
  if (weekEndExclusive === null
    || ![header.providerLabel, header.clientLabel, header.siteLabel].every((label) => isClientArtifactPrintableLabel(label))
    || !isClientArtifactPreparedBy(header.preparedByLabel, header.preparedByRole) || !isServiceTimeZone(header.timeZone)
    || header.weekStart !== key.weekStart || header.weekEndExclusive !== weekEndExclusive
    || !isServiceInstant(header.generatedAt) || !isServiceInstant(header.dataThroughAt)) return false;
  const receipt = value.approval.receipt;
  if (receipt.approvalState !== "recorded" || !isServiceInstant(receipt.approvedAt) || !isSummary(receipt.summary)
    || header.dataThroughAt !== receipt.approvedAt || Date.parse(header.generatedAt) < Date.parse(receipt.approvedAt)) return false;
  const approved = receipt.summary;
  if (!isSummary(value.coverageSummary)) return false;
  const summary = value.coverageSummary;
  if (!SUMMARY_KEYS.every((field) => summary[field] === approved[field])) return false;

  if (!Array.isArray(value.checkpointExceptions) || value.checkpointExceptions.length > 10_000
    || !value.checkpointExceptions.every((row) => isCheckpointException(row))) return false;
  const exceptions = value.checkpointExceptions as ServiceWeekCheckpointRow[];
  if (exceptions.some((row, index) => index > 0 && row.rowOrdinal <= exceptions[index - 1]!.rowOrdinal)) return false;
  // The subset must reconcile against the frozen population counts it was drawn from.
  const missing = exceptions.filter((row) => row.kind === "required").length;
  if (missing !== summary.missedCheckInCount || exceptions.length - missing !== summary.checkpointExceptionCount) return false;

  if (!Array.isArray(value.incidents) || value.incidents.length !== summary.incidentCount
    || !value.incidents.every((row) => isIncident(row, key.includeMediaCounts, key.weekStart, weekEndExclusive))) return false;
  const incidents = value.incidents as DutyActivityIncident[];
  if (incidents.some((row, index) => row.rowOrdinal !== index + 1)) return false;
  if (incidents.some((row, index) => index > 0 && Date.parse(row.occurredAt) < Date.parse(incidents[index - 1]!.occurredAt))) return false;

  if (!key.includeMediaCounts) return value.mediaTotals === null;
  if (!isMediaCounts(value.mediaTotals)) return false;
  const totals = value.mediaTotals;
  return MEDIA_KINDS.every((kind) => incidents.reduce((sum, row) => sum + (row.mediaCounts?.[kind] ?? 0), 0) === totals[kind])
    && mediaTotal(totals) <= 1_000_000;
}

function refusal(value: Record<string, unknown>): value is DutyActivityRefusal | DutyActivityUnavailable {
  return exact(value, ["status", "reason"])
    && (value.status === "refused" && (value.reason === "week_unapproved" || value.reason === "no_scheduled_service")
      || value.status === "unavailable" && ["too_large", "target_unavailable", "source_incomplete", "unavailable"].includes(String(value.reason)));
}

/** Pin the validated object against accidental in-process changes after Preview. */
function freezePrintable<T>(value: T): T {
  if (value !== null && typeof value === "object") {
    for (const child of Object.values(value)) freezePrintable(child);
    Object.freeze(value);
  }
  return value;
}

export function parseDutyActivityReportRead(value: unknown, key: DutyActivityKey): DutyActivityReadResult {
  try {
    if (!record(value) || !isDutyActivityKey(key)) return unavailable();
    if (refusal(value)) return value;
    return exact(value, ["status", "revision", "report"]) && value.status === "ok" && revision(value.revision) && isReport(value.report, key)
      ? { status: "ok", revision: value.revision, report: freezePrintable(value.report) } : unavailable();
  } catch { return unavailable(); }
}

export function parseDutyActivityReportRevalidation(value: unknown): DutyActivityRevalidationResult {
  try {
    if (!record(value)) return unavailable();
    if (refusal(value)) return value;
    return exact(value, ["status"]) && (value.status === "current" || value.status === "changed") ? { status: value.status } : unavailable();
  } catch { return unavailable(); }
}

const argsFor = (key: DutyActivityKey) => ({
  p_account_id: key.accountId, p_client_id: key.clientId, p_site_id: key.siteId, p_week_start: key.weekStart,
  p_variant: key.variant, p_include_media_counts: key.includeMediaCounts,
});

export async function fetchDutyActivityReportWeek(client: SupabaseClient, key: DutyActivityKey): Promise<DutyActivityReadResult> {
  if (!isDutyActivityKey(key)) return unavailable();
  try {
    const { data, error } = await client.rpc("read_duty_activity_report_week", argsFor(key));
    return error ? unavailable() : parseDutyActivityReportRead(data, key);
  } catch { return unavailable(); }
}

export async function revalidateDutyActivityReportWeek(
  client: SupabaseClient, key: DutyActivityKey, expectedRevision: string,
): Promise<DutyActivityRevalidationResult> {
  if (!isDutyActivityKey(key) || !revision(expectedRevision)) return unavailable();
  try {
    const { data, error } = await client.rpc("revalidate_duty_activity_report_week", { ...argsFor(key), p_expected_revision: expectedRevision });
    return error ? unavailable() : parseDutyActivityReportRevalidation(data);
  } catch { return unavailable(); }
}
